import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "./smooth";
import { useT } from "@/lib/i18n";

const IMG = [["/img/engine.webp", "tuning"], ["/img/bench.webp", "ecu"], ["/img/hero.webp", "ecu"], ["/img/keys.webp", "bilnokler"], ["/img/cockpit.webp", "bilnokler"], ["/img/engine.webp", "tuning"]];
const TXT = {
  no: {
    rows: [
      ["Chiptuning", "Stage 1 tuning i Stavanger – med Stage 1-kalkulator"],
      ["ECU / TCU", "ECU- og TCU-programmering, ECU-kloning og reparasjon av styreenheter"],
      ["Bildiagnostikk", "Bilelektronikk og avansert feilsøking"],
      ["Bilnøkler", "Programmering av bilnøkler, keyless og alle bilnøkler mistet"],
      ["Bilåpning", "Når du har låst deg ute av bilen"],
      ["AdBlue · DPF · EGR", "Feilsøking og løsninger"],
    ],
    eyebrow: "Tjenester", h2a: "Alt under", h2b: "panseret.",
    p: "Chiptuning, bilnøkler, ECU/TCU-programmering og bildiagnose – velg en tjeneste for å lese mer.",
  },
  en: {
    rows: [
      ["Chiptuning", "Stage 1 tuning in Stavanger – with Stage 1 calculator"],
      ["ECU / TCU", "ECU and TCU programming, ECU cloning and control unit repair"],
      ["Diagnostics", "Car electronics and advanced troubleshooting"],
      ["Car keys", "Car key programming, keyless and all keys lost"],
      ["Car opening", "When you have locked yourself out of the car"],
      ["AdBlue · DPF · EGR", "Troubleshooting and solutions"],
    ],
    eyebrow: "Services", h2a: "Everything under", h2b: "the bonnet.",
    p: "Chiptuning, car keys, ECU/TCU programming and diagnostics – choose a service to read more.",
  },
};

const Row = ({ r, i }) => (
  <motion.a href={`/#${r.to}`} onClick={(e) => { e.preventDefault(); scrollToId(r.to); }} data-testid={`services-index-row-${i}`}
    initial="rest" whileHover="hover" animate="rest"
    className="group relative grid w-full grid-cols-[auto_1fr_auto] items-center gap-4 overflow-hidden border-t border-white/10 py-6 text-left md:gap-8 md:py-8">
    <motion.span variants={{ rest: { scaleY: 0 }, hover: { scaleY: 1 } }} transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 origin-bottom bg-[var(--red)]" />
    <span className="relative font-mono text-xs text-zinc-400 transition-colors group-hover:text-white md:pl-4">0{i + 1}</span>
    <span className="relative flex min-w-0 items-center gap-4 md:gap-8">
      <span className="h-12 w-16 shrink-0 overflow-hidden md:h-16 md:w-24">
        <img src={r.img} alt="" loading="lazy" className="h-full w-full object-cover grayscale transition-[filter,transform] duration-500 group-hover:scale-110 group-hover:grayscale-0" />
      </span>
      <span className="min-w-0">
        <span className="block font-display text-3xl font-black uppercase leading-none transition-transform duration-500 group-hover:translate-x-2 md:text-6xl">{r.t}</span>
        <span className="mt-1 block text-sm text-zinc-400 transition-colors group-hover:text-white">{r.d}</span>
      </span>
    </span>
    <ArrowUpRight className="relative shrink-0 transition-transform duration-500 group-hover:rotate-45 md:mr-4" size={28} />
  </motion.a>
);

export const ServicesIndex = () => {
  const t = useT(TXT);
  const ROWS = t.rows.map(([title, d], i) => ({ t: title, d, img: IMG[i][0], to: IMG[i][1] }));
  return (
  <section id="tjenester" data-testid="services-index-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36">
    <Reveal className="mb-12 grid gap-6 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <Eyebrow index="01">{t.eyebrow}</Eyebrow>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{t.h2a}<br /><span className="stroke-text">{t.h2b}</span></h2>
      </div>
      <p className="text-base leading-relaxed text-zinc-300 md:col-span-5">
        {t.p}
      </p>
    </Reveal>
    <Reveal>
      <div className="border-b border-white/10">
        {ROWS.map((r, i) => <Row key={i} r={r} i={i} />)}
      </div>
    </Reveal>
  </section>
  );
};
